/**
 * The teams on the dispatch bar, folded into one drop-down per kind of team
 * — RFF, airside operations, sweepers, AGL teams, maintenance, security,
 * Civil Defence, safety officers, equipment — instead of a chip per team
 * across the top of the map. Each button says how many are out; opening it
 * gives the same SEND, INSPECT and RECALL the chips had.
 *
 * Beside them, whether the aircraft movement area is clear: every vehicle on
 * a runway or taxiway and why it is there. A team is allowed there only for a
 * job or an inspection; released or recalled, it is vacating; anything else is
 * an obstacle.
 *
 * Shared by the Interactive Map and the Dynamic map, so the two bars cannot
 * drift apart.
 */
import { useEffect, useRef, useState } from "react";
import { t } from "../../i18n";
import { byCategory, movementArea, type MovementWhy, type TeamRow } from "../domain/dispatch";
import s from "./TeamGroups.module.css";

const WHY_LABEL: Record<MovementWhy, string> = {
  working: "working on {wo}",
  inspecting: "inspecting",
  vacating: "vacating",
  obstacle: "no job — must leave",
};

export function TeamGroups({
  rows,
  canSend,
  runways,
  onSend,
  onRecall,
  onInspect,
}: {
  rows: readonly TeamRow[];
  /** a target is picked, so SEND means something */
  canSend: boolean;
  runways: readonly string[];
  onSend: (k: string) => void;
  onRecall: (k: string) => void;
  onInspect: (k: string, rwy: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const groups = byCategory(rows);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(null);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className={s.groups} ref={wrap} data-testid="team-groups">
      {groups.map((g) => {
        const out = g.rows.filter((r) => r.state !== "available").length;
        const isOpen = open === g.id;
        return (
          <div key={g.id} className={s.group}>
            <button
              type="button"
              className={s.head}
              aria-expanded={isOpen}
              data-out={out > 0}
              data-testid={`team-group-${g.id}`}
              onClick={() => setOpen(isOpen ? null : g.id)}
            >
              <span className={s.name}>{t(g.label)}</span>
              <span className={s.count}>{g.rows.length}</span>
              {out > 0 && <span className={s.out}>{t("{n} out", { n: out })}</span>}
              <span className={s.caret} aria-hidden="true">
                ▾
              </span>
            </button>
            {isOpen && (
              <div className={s.panel} role="group" aria-label={t(g.label)}>
                {g.rows.map((tm) => (
                  <div key={tm.k} className={s.row} data-team={tm.k}>
                    <div className={s.rowHead}>
                      <span className={s.dot} style={{ background: tm.ring }} />
                      <b>{tm.name}</b>
                      <span className={s.kind}>{tm.crew || tm.kind}</span>
                    </div>
                    <div className={s.state} data-moving={tm.spd > 0}>
                      {tm.moveLabel || tm.label}
                      {tm.spd > 0 ? ` · ${tm.spd} km/h` : ""}
                      {tm.eta && tm.state === "responding" ? ` · ${tm.eta.mins} min` : ""}
                    </div>
                    <div className={s.actions}>
                      <button
                        type="button"
                        className={s.send}
                        disabled={!canSend}
                        title={canSend ? undefined : t("Pick a target on the map first")}
                        onClick={() => onSend(tm.k)}
                      >
                        {t("SEND")}
                      </button>
                      {/operations/i.test(tm.kind) &&
                        runways.map((r) => (
                          <button key={r} type="button" className={s.btn} onClick={() => onInspect(tm.k, r)}>
                            {t("INSPECT {r}", { r })}
                          </button>
                        ))}
                      {tm.state !== "available" && (
                        <button type="button" className={s.btn} onClick={() => onRecall(tm.k)}>
                          {t("RECALL")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {!rows.length && <span className={s.kind}>{t("Loading the teams…")}</span>}
    </div>
  );
}

/** Whether the runways and taxiways are clear, and who is on them if not. */
export function MovementAreaStatus({ rows }: { rows: readonly TeamRow[] }) {
  const m = movementArea(rows);
  const tone = m.clear ? "clear" : m.obstacles ? "obstacle" : "busy";
  return (
    <div className={s.movement} data-tone={tone} data-testid="movement-area" role="status">
      <b>
        {m.clear
          ? t("MOVEMENT AREA CLEAR")
          : m.obstacles
            ? t("MOVEMENT AREA NOT CLEAR")
            : t("{n} ON THE MOVEMENT AREA", { n: m.onIt.length })}
      </b>
      {m.onIt.map(({ row, why }) => (
        <span key={row.k} className={s.onIt} data-why={why}>
          {row.name} — {t(WHY_LABEL[why], { wo: row.wo ?? "" })}
        </span>
      ))}
      <span className={s.rule}>
        {t(
          "Released or recalled, all teams and equipment leave the runways and taxiways at once.",
        )}
      </span>
    </div>
  );
}
